import { translate } from "app/filters/i18nFilter";
import type { PremiumFeature } from "app/pages/sales-intelligence/pages/premium-features/types";
import { PremiumFeatures } from "app/pages/sales-intelligence/pages/premium-features/types/enums";
import { AssetsService } from "app/services/AssetsService";

export const LARGE_CARD_WIDTH = 672;
export const STANDARD_CARD_WIDTH = 440;

export const PREMIUM_FEATURES_FILTERS: string[] = [
    "AI/GenAI",
    "Collaboration",
    "Enrichment",
    "API",
    "Operations",
    "Workflows",
    "Lead scoring",
    "Automations",
    "Qualification",
    "Prospecting",
];

export const getMostPopularPremiumFeatures = (): readonly PremiumFeature[] => [
    {
        title: translate("si.premium-feature.contact-api.title"),
        description: translate("si.premium-feature.contact-api.description"),
        imageUrl: AssetsService.assetUrl("/images/premium-feature/feature-contacts-api.svg"),
        userCount: "1000+ Users",
        tags: ["Enrichment", "Operations", "Prospecting", "API", "Integrations"],
        badge: "new",
        type: PremiumFeatures.ContactApi,
    },
    {
        title: translate("si.premium-feature.lead-enrichment-api.title"),
        description: translate("si.premium-feature.lead-enrichment-api.description"),
        imageUrl: AssetsService.assetUrl("/images/premium-feature/feature-lead-api.svg"),
        userCount: "3000+ Users",
        tags: ["Enrichment", "Operations", "Prospecting", "API", "Integrations", "Lead scoring", "Qualification"],
        type: PremiumFeatures.LeadsEnrichmentApi,
    },
    {
        title: translate("si.premium-feature.signals-api.title"),
        description: translate("si.premium-feature.signals-api.description"),
        imageUrl: AssetsService.assetUrl("/images/premium-feature/feature-signals.svg"),
        userCount: "4500+ Users",
        tags: ["API", "Integrations", "Operations", "Workflows", "Lead scoring", "Automations"],
        type: PremiumFeatures.SignalsApi,
    },
];

export const getNtMostPopularPremiumFeatures = (): readonly PremiumFeature[] => [
    {
        title: translate("si.premium-feature.additional-users.title"),
        description: translate("si.premium-feature.additional-users.description"),
        imageUrl: AssetsService.assetUrl("/images/premium-feature/feature-get-more-users.svg"),
        userCount: "1000+ Users",
        tags: ["Collaboration"],
        badge: "new",
        type: PremiumFeatures.AdditionalUsers,
    },
    {
        title: translate("si.premium-feature.data-credits.title"),
        description: translate("si.premium-feature.additional-users.description"),
        imageUrl: AssetsService.assetUrl("/images/premium-feature/feature-get-more-credits.svg"),
        userCount: "1000+ Users",
        tags: ["API", "Enrichment", "Qualification"],
        badge: "new",
        type: PremiumFeatures.DataCredits,
    },
];

export const getBasePremiumFeatures = (): readonly PremiumFeature[] => [
    {
        title: translate("si.premium-feature.country-filters.title"),
        description: translate("si.premium-feature.country-filters.description"),
        icon: "country-filter-gradient",
        userCount: "50K+ Users",
        tags: ["Prospecting", "Qualification"],
        type: PremiumFeatures.CountryFilters,
    },
    {
        title: translate("si.premium-feature.apps-sdk-data.title"),
        description: translate("si.premium-feature.apps-sdk-data.description"),
        icon: "mobile-apps-gradient",
        userCount: "20K+ Users",
        tags: ["Prospecting", "Qualification", "Enrichment"],
        badge: "new",
        type: PremiumFeatures.AppsSdkData,
    },
    {
        title: translate("si.premium-feature.integrations.title"),
        description: translate("si.premium-feature.integrations.description"),
        icon: "plug-in-gradient",
        userCount: "700+ Users",
        tags: [
            "Enrichment",
            "Operations",
            "Prospecting",
            "API",
            "Integrations",
            "Workflows",
            "Lead scoring",
            "Automations",
        ],
        type: PremiumFeatures.Integrations,
    },
    {
        title: translate("si.premium-feature.enrichment.title"),
        description: translate("si.premium-feature.enrichment.description"),
        icon: "crm-record-gradient",
        userCount: "40K+ Users",
        tags: [
            "Prospecting",
            "Qualification",
            "Enrichment",
            "API",
            "Integrations",
            "Workflows",
            "Lead scoring",
            "Automations",
        ],
        type: PremiumFeatures.Enrichment,
    },
    {
        title: translate("si.premium-feature.extension-integration.title"),
        description: translate("si.premium-feature.extension-integration.description"),
        icon: "extension-gradient",
        userCount: "1200+ Users",
        tags: ["Integrations", "Enrichment", "Prospecting", "Qualification", "Workflows"],
        type: PremiumFeatures.ExtensionIntegration,
    },
    {
        title: translate("si.premium-feature.ai-outreach.title"),
        description: translate("si.premium-feature.ai-outreach.description"),
        icon: "ai-sparkless-gradient",
        userCount: "3000+ Users",
        tags: ["AI/GenAI", "Prospecting", "Qualification", "Automations"],
        type: PremiumFeatures.AiOutreach,
    },
    {
        title: translate("si.premium-feature.ai-prospecting.title"),
        description: translate("si.premium-feature.ai-prospecting.description"),
        icon: "ai-sparkless-gradient",
        userCount: "1200+ Users",
        tags: ["AI/GenAI", "Prospecting", "Qualification", "Automations"],
        type: PremiumFeatures.AiProspecting,
    },
    {
        title: translate("si.premium-feature.prepare-meeting.title"),
        description: translate("si.premium-feature.prepare-meeting.description"),
        icon: "ai-sparkless-gradient",
        userCount: "1000+ Users",
        tags: ["AI/GenAI", "Prospecting", "Qualification", "Automations"],
        type: PremiumFeatures.PrepareMeetings,
    },
];

export const getAllPremiumFeatures = (): readonly PremiumFeature[] => [
    {
        title: translate("si.premium-feature.additional-users.title"),
        description: translate("si.premium-feature.additional-users.description"),
        icon: "Demographics",
        userCount: "3000+ Users",
        tags: ["Collaboration"],
        type: PremiumFeatures.AdditionalUsers,
    },
    {
        title: translate("si.premium-feature.data-credits.title"),
        description: translate("si.premium-feature.data-credits.description"),
        icon: "market-trends-gradient",
        userCount: "35K+ Users",
        tags: ["API", "Integrations", "Qualification", "Enrichment", "Operations", "Prospecting"],
        type: PremiumFeatures.DataCredits,
    },
    {
        title: translate("si.premium-feature.contact-credits.title"),
        description: translate("si.premium-feature.contact-credits.description"),
        icon: "Demographics",
        userCount: "700+ Users",
        tags: ["Enrichment", "Operations", "Prospecting", "API", "Integrations"],
        type: PremiumFeatures.ContactCredits,
    },
    ...getBasePremiumFeatures(),
];
